// SiteSettingsContext · publishes runtime admin-controlled settings
// (currently: which tier is "featured" + the badge label) to every
// marketing component that needs to read them.
//
// The context fetches /api/site-settings on mount, caches the result
// locally, and exposes:
//   { featuredTier, featuredLabel, getBadgeFor(tier), refresh() }
//
// Cross-tab propagation:
//   • localStorage event listener on key "ofm-site-settings-bump" so
//     a save in /admin/dashboard immediately re-fetches in any open
//     tab without a manual reload.
//   • A custom "ofm-site-settings-changed" window event for same-tab
//     bumps (e.g. the SettingsTab dispatching after a successful POST).
//
// Robustness:
//   • Errors are console.error'd (not silenced) so QA can see fetch
//     failures in DevTools.
//   • Cache-busting timestamp on the request so a stale CDN response
//     never sticks.
//   • Defaults to premier / "Most Popular" if the endpoint never
//     resolves so the UI degrades gracefully.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ACCOUNTS as DEFAULT_ACCOUNTS } from "@/data/site";

const SiteSettingsContext = createContext({
  featuredTier: "premier",
  featuredLabel: "Most Popular",
  signupWizardEnabled: false,
  ctaRouting: {},
  accounts: DEFAULT_ACCOUNTS,
  loaded: false,
  getBadgeFor: () => undefined,
  getAccount: () => null,
  refresh: () => {},
});

export function SiteSettingsProvider({ children }) {
  const [state, setState] = useState({
    featuredTier: "premier",
    featuredLabel: "Most Popular",
    signupWizardEnabled: false,
    ctaRouting: {},
    accountOverrides: {},
    branding: {},
    loaded: false,
  });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get(`/site-settings?_=${Date.now()}`);
      const data = res?.data;
      if (data && typeof data === "object") {
        setState({
          featuredTier: data.featured_tier || "premier",
          featuredLabel: data.featured_label || "Most Popular",
          signupWizardEnabled: Boolean(data.signup_wizard_enabled),
          ctaRouting: (data.cta_routing && typeof data.cta_routing === "object") ? data.cta_routing : {},
          accountOverrides: (data.accounts && typeof data.accounts === "object") ? data.accounts : {},
          branding: (data.branding && typeof data.branding === "object") ? data.branding : {},
          loaded: true,
        });
      } else {
        setState((s) => ({ ...s, loaded: true }));
      }
    } catch (e) {
      console.error("[SiteSettings] fetch failed:", e?.message || e);
      // Mark loaded even on failure so deep-link effects can fall through
      // to their disabled-wizard branch instead of hanging forever.
      setState((s) => ({ ...s, loaded: true }));
    }
  }, []);

  // Initial fetch on mount.
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Cross-tab + same-tab bump listener · the SettingsTab dispatches
  // these after a successful POST /api/admin/settings so all open
  // tabs refresh without manual reload.
  useEffect(() => {
    const onChange = () => fetchSettings();
    window.addEventListener("ofm-site-settings-changed", onChange);
    const onStorage = (e) => {
      if (e.key === "ofm-site-settings-bump") fetchSettings();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("ofm-site-settings-changed", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [fetchSettings]);

  // ── Branding overrides ─────────────────────────────────────────────
  // Admin-tunable global gradient palette. Injects a <style> tag at the
  // END of <head> so the custom-property values win the cascade over the
  // identical-specificity defaults in index.css. Only valid 6-digit hex
  // values are applied; everything else falls back to the CSS defaults.
  useEffect(() => {
    const HEX = /^#[0-9a-fA-F]{6}$/;
    const b = state.branding || {};
    const lines = [];
    if (HEX.test(b.light_brand) || HEX.test(b.light_brand_deep)) {
      const vars = [
        HEX.test(b.light_brand) ? `--brand: ${b.light_brand};` : "",
        HEX.test(b.light_brand_deep) ? `--brand-deep: ${b.light_brand_deep};` : "",
        HEX.test(b.light_brand) ? `--primary: ${hexToHslTriplet(b.light_brand)};` : "",
        HEX.test(b.light_brand) ? `--ring: ${hexToHslTriplet(b.light_brand)};` : "",
      ].join(" ");
      lines.push(`html.light { ${vars} }`);
    }
    if (HEX.test(b.dark_brand) || HEX.test(b.dark_brand_deep)) {
      const vars = [
        HEX.test(b.dark_brand) ? `--brand: ${b.dark_brand};` : "",
        HEX.test(b.dark_brand_deep) ? `--brand-deep: ${b.dark_brand_deep};` : "",
      ].join(" ");
      lines.push(`html.dark { ${vars} }`);
    }
    let tag = document.getElementById("ofm-branding-overrides");
    if (lines.length === 0) {
      if (tag) tag.remove();
      return;
    }
    if (!tag) {
      tag = document.createElement("style");
      tag.id = "ofm-branding-overrides";
    }
    tag.textContent = lines.join("\n");
    // Re-append on every change so the tag stays LAST in <head> (wins
    // the cascade even if other style tags were added after it).
    document.head.appendChild(tag);
  }, [state.branding]);

  // Merge per-account overrides on top of the static defaults so
  // marketing copy stays in sync with whatever ops needs to change
  // from /admin/dashboard without a redeploy.
  const accounts = useMemo(() => {
    const out = {};
    for (const k of Object.keys(DEFAULT_ACCOUNTS)) {
      out[k] = { ...DEFAULT_ACCOUNTS[k], ...(state.accountOverrides[k] || {}) };
    }
    return out;
  }, [state.accountOverrides]);

  const value = useMemo(() => ({
    featuredTier: state.featuredTier,
    featuredLabel: state.featuredLabel,
    signupWizardEnabled: state.signupWizardEnabled,
    ctaRouting: state.ctaRouting,
    accounts,
    loaded: state.loaded,
    refresh: fetchSettings,
    // Returns the merged account dict for a tier key, or null if the
    // key doesn't match any known account.
    getAccount: (tierKey) => accounts[tierKey] || null,
    // Returns the badge to render for a given tier key. If the tier
    // matches the admin-featured tier we return featuredLabel,
    // otherwise we fall back to the static `staticBadge` arg (passed
    // by callers from ACCOUNTS[tier].badge) so the non-featured tier
    // can still display its own static badge (e.g. Elite "Premium").
    getBadgeFor: (tierKey, staticBadge) => {
      if (state.featuredTier === tierKey && state.featuredLabel) {
        return state.featuredLabel;
      }
      // Suppress the legacy "Most Popular" hard-coded badge on tiers
      // that are NOT the currently-featured tier so we don't end up
      // with two "Most Popular" labels at once.
      if (staticBadge === "Most Popular" && state.featuredTier !== tierKey) {
        return undefined;
      }
      return staticBadge;
    },
  }), [state, accounts, fetchSettings]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

// "#15648f" → "205 65% 32%" (shadcn HSL triplet format, no hsl() wrapper)
function hexToHslTriplet(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Helper that the SettingsTab calls after a successful save so the
// running tab AND any other open tabs immediately reflect the new
// featured tier / badge label without a manual reload.
export function broadcastSiteSettingsChange() {
  try {
    window.dispatchEvent(new CustomEvent("ofm-site-settings-changed"));
    // localStorage bump for cross-tab — the value doesn't matter, only
    // the change event matters.
    window.localStorage.setItem("ofm-site-settings-bump", String(Date.now()));
  } catch (_) { /* localStorage may be disabled · safe to ignore */ }
}
